// Utility functions for integration settings

/**
 * Formats date for form data
 * @param {Array} value - Date range array
 * @param {String} intId - Integration ID
 * @returns {Object} - Formatted date object
 */
export const formatDateForFormData = (value, intId) => {
  if (!value)
    return { from_date: '', to_date: '', integration_id: intId, value: '' };

  const fromDate =
    (value[0].getMonth() > 8
      ? value[0].getMonth() + 1
      : '0' + (value[0].getMonth() + 1)) +
    '-' +
    (value[0].getDate() > 9 ? value[0].getDate() : '0' + value[0].getDate()) +
    '-' +
    value[0].getFullYear();

  const toDate =
    (value[1].getMonth() > 8
      ? value[1].getMonth() + 1
      : '0' + (value[1].getMonth() + 1)) +
    '-' +
    (value[1].getDate() > 9 ? value[1].getDate() : '0' + value[1].getDate()) +
    '-' +
    value[1].getFullYear();

  return {
    from_date: fromDate,
    to_date: toDate,
    integration_id: intId,
    value: value,
  };
};

/**
 * Renames a key in an object
 * @param {Object} obj - Object to modify
 * @param {String} oldKey - Old key name
 * @param {String} newKey - New key name
 */
export const renameKey = (obj, oldKey, newKey) => {
  obj[newKey] = obj[oldKey];
  delete obj[oldKey];
};

/**
 * Creates a simple object with a key-value pair
 * @param {String} key - Object key
 * @param {any} value - Object value
 * @returns {Object} - Created object
 */
export const objToJson = (key, value) => {
  const res = {};
  res[key] = value;
  return res;
};

/**
 * Checks if an integration is connected
 *
 * This is a pure function with no side effects - safe to use in array methods
 * like .some(), .filter(), .map(), etc.
 *
 * @param {Object} item - Integration item
 * @param {string} item.integration_type - Type of integration
 * @param {boolean} [item.refresh_token_expired] - Whether refresh token is expired
 * @returns {Boolean} - Connection status (true if connected, false otherwise)
 */
export const checkConnection = item => {
  if (item.integration_type === 'offline') {
    return false;
  } else {
    return item.refresh_token_expired !== true;
  }
};

/**
 * Returns currency string from country ID
 * @param {String} countryInput - Country ID
 * @param {Array} countryData - Country data array
 * @returns {String} - Currency string
 */
export const returnCurrencyFromCountry = (countryInput, countryData) => {
  if (!countryData) return '';

  let currency = '';
  countryData.forEach(obj => {
    if (obj.value == countryInput) {
      currency = obj.country_label + ' (' + obj.currency + ')';
    }
  });

  return currency;
};

/**
 * Updates Heap Analytics event properties with integration connection status
 *
 * This function checks if any integration is connected to accounting software
 * and updates the global Heap event property 'integrationConnected' accordingly.
 * This property will be sent with all subsequent Heap events.
 *
 * Benefits of using this shared function:
 * - Single source of truth for integration connection status logic
 * - Consistent behavior across all components
 * - Easier to maintain and update
 * - Reduces code duplication
 *
 * @param {Array} integrations - Array of integration objects
 * @param {import('../../../utils/analytics/heapAnalytics').addEventProperties} addEventProperties - Heap addEventProperties function
 *
 * @example
 * import { updateIntegrationConnectionStatus } from './integrationUtils';
 * import { addEventProperties } from '../../../utils/analytics/heapAnalytics';
 *
 * // After fetching integrations
 * const data = await fetchIntegrations();
 * updateIntegrationConnectionStatus(data.integration, addEventProperties);
 */
export const updateIntegrationConnectionStatus = (
  integrations,
  addEventProperties
) => {
  try {
    // Check if any integration is connected to accounting software
    // Using .some() for efficiency - short-circuits on first connected integration
    // checkConnection is a pure function, safe to use in array iteration
    const hasConnectedIntegration =
      integrations &&
      integrations.length > 0 &&
      integrations.some(app => checkConnection(app));

    // Update Heap event properties with the connection status
    // This property will be sent with all subsequent events
    addEventProperties({
      integrationConnected: hasConnectedIntegration,
    });
  } catch (error) {
    console.error(
      '[Integration Utils] Error updating integration connection status:',
      error
    );
  }
};
